// frontend/src/services/pixApi.js
//
// Chamadas do Souli à ponte /api/pix. Fica separado do apiFetch DE PROPÓSITO: o apiFetch derruba a sessão em qualquer
// 401/403, e aqui "sem permissão no Pix" (403 com code PIX_ACCESS_DENIED) precisa virar uma mensagem na página,
// não um logout. Um 403 de token inválido (do authenticateToken) continua encerrando a sessão.

const PIX_BASE = '/api/pix';

/** Erro das chamadas ao Pix: `code` identifica o motivo (PIX_ACCESS_DENIED, VALIDATION, CLIENT_EXISTS, NETWORK...). */
export class PixApiError extends Error {
  constructor(message, { status = 0, code = null, fields = null, data = null } = {}) {
    super(message);
    this.name = 'PixApiError';
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.data = data;
  }
}

const endSession = () => {
  localStorage.removeItem('token');
  window.location.href = '/login';
};

/**
 * Chamada autenticada a /api/pix<path>.
 * @param {string} method GET ou POST.
 * @param {string} path Caminho depois de /api/pix (ex.: "/recurrences").
 * @param {{params?: Object, body?: any, uncertainMessage?: string}} [options] params: consulta (vazios são ignorados); body: corpo JSON;
 *   uncertainMessage: texto para quando a conexão cai numa gravação (não dá para saber se o servidor gravou).
 * @returns {Promise<any>} Corpo JSON da resposta.
 * @throws {PixApiError}
 */
async function pixRequest(method, path, { params = {}, body, uncertainMessage } = {}) {
  const token = localStorage.getItem('token');
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') query.append(key, String(value));
  });
  const url = PIX_BASE + path + (query.toString() ? `?${query.toString()}` : '');
  const hasBody = body !== undefined;

  let response;
  try {
    response = await fetch(url, {
      ...(method !== 'GET' && { method }),
      headers: {
        Accept: 'application/json',
        ...(hasBody && { 'Content-Type': 'application/json' }),
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      ...(hasBody && { body: JSON.stringify(body) }),
    });
  } catch (error) {
    // Numa gravação, a conexão pode cair DEPOIS de o servidor ter salvo: não dá para saber.
    if (method !== 'GET') {
      throw new PixApiError(uncertainMessage || 'A conexão caiu e não sei se o cadastro foi salvo. Confira em Consulta de Clientes antes de tentar de novo.', { code: 'NETWORK_UNCERTAIN' });
    }
    throw new PixApiError('Sem conexão com o servidor do Souli. Verifique a internet e tente novamente.', { code: 'NETWORK' });
  }

  let data = null;
  try {
    data = await response.json();
  } catch (error) {
    data = null;
  }
  if (response.ok) return data;

  const code = data && data.code ? data.code : null;
  const message = (data && data.error) || (method === 'GET' ? 'Erro ao consultar o Pix.' : 'Não foi possível concluir a operação. Tente novamente.');
  const accessDenied = code === 'PIX_ACCESS_DENIED' || /acesso negado (ao (m[óo]dulo pix|cadastro de clientes pix)|[àa] cobran[çc]a pix)/i.test(message);

  if (response.status === 401 || (response.status === 403 && !accessDenied)) {
    endSession();
    throw new PixApiError('Sessão expirada', { status: response.status, code: 'SESSION' });
  }
  throw new PixApiError(message, {
    status: response.status,
    code: accessDenied ? 'PIX_ACCESS_DENIED' : code,
    fields: data && data.fields ? data.fields : null,
    data,
  });
}

/** GET em /api/pix<path>. */
export const pixGet = (path, params = {}) => pixRequest('GET', path, { params });

/** POST em /api/pix<path> com corpo JSON. `options.uncertainMessage`: texto para conexão caída no meio da gravação. */
export const pixPost = (path, body, options = {}) => pixRequest('POST', path, { body, ...options });

/** Planos do Pix (lista de { id, name, description, amount }). */
export async function getPixProducts() {
  const data = await pixGet('/products');
  return Array.isArray(data && data.products) ? data.products : [];
}

/** Cadastra um cliente Pix no Souli (o servidor valida tudo). Devolve { client }. */
export const registerPixClient = (payload) => pixPost('/clients', payload);

/** Cria uma recorrência Pix (jornada 3 ou 4) para um cliente do Souli. Devolve { recurrence, qr, warning, recovered, ... }. */
export const createPixRecurrence = (payload) => pixPost('/recurrences', payload, {
  uncertainMessage: 'A conexão caiu e não sei se a recorrência foi criada. Confira em Cobranças → Pix antes de tentar de novo.',
});

/** Consulta se o QR da recorrência já está pronto. Devolve { ready, qr }. */
export const getPixQr = (recurrenceId) => pixGet(`/recurrences/${encodeURIComponent(recurrenceId)}/qr`);

/** Cria (ou reaproveita) o link público do QR para enviar ao cliente. Devolve { url, expiresAt, reused }. */
export const createPixShare = (recurrenceId) => pixPost(`/recurrences/${encodeURIComponent(recurrenceId)}/share`, {}, {
  uncertainMessage: 'A conexão caiu e não sei se o link foi gerado. Tente de novo.',
});

/** Apaga um cliente Pix do Souli (só administrador; o servidor recusa se houver recorrência em aberto no Pix). */
export const deletePixClient = (clientId) => pixRequest('DELETE', `/clients/${encodeURIComponent(clientId)}`, {
  uncertainMessage: 'A conexão caiu e não sei se o cliente foi apagado. Atualize a lista antes de tentar de novo.',
});

/** Cancela uma recorrência Pix no Santander (o servidor confere a permissão e o estado, e nunca repete sozinho). */
export const cancelPixRecurrence = (recurrenceId) => pixPost(`/recurrences/${encodeURIComponent(recurrenceId)}/cancel`, {}, {
  uncertainMessage: 'A conexão caiu e não sei se a recorrência foi cancelada. Atualize a lista e confira o status antes de tentar de novo.',
});

/** Tenta de novo a cobrança inicial de uma recorrência das jornadas 3 ou 4 que ficou sem ela. */
export const retryPixCob = (recurrenceId) => pixPost(`/recurrences/${encodeURIComponent(recurrenceId)}/retry-cob`, {}, {
  uncertainMessage: 'A conexão caiu e não sei se a cobrança inicial foi criada. Atualize a lista e confira antes de tentar de novo.',
});

/** Cria uma cobrança avulsa (Pix único) para um cliente do Souli. Devolve { charge, qr, recovered, ... }. */
export const createPixCharge = (payload) => pixPost('/charges', payload, {
  uncertainMessage: 'A conexão caiu e não sei se a cobrança foi criada. Confira no Pix antes de tentar de novo.',
});

/** Cria (ou reaproveita) o link público do QR de uma cobrança avulsa. Devolve { url, expiresAt, reused }. */
export const createPixChargeShare = (chargeId) => pixPost(`/charges/${encodeURIComponent(chargeId)}/share`, {}, {
  uncertainMessage: 'A conexão caiu e não sei se o link foi gerado. Tente de novo.',
});
